/**
 * Login hook (TASK-632: renamed from useDemoLogin — this is THE general login).
 *
 * - Email + password against POST /api/auth/login (canonical since TASK-632;
 *   /auth/api/demo/login remains a backend alias one release).
 * - TASK-631: a tenant with require_2fa returns {status, challenge_token} instead
 *   of tokens; the caller routes to /login/2fa and finishes via finalizeSession().
 * - Token storage + account/user context update on success.
 */

import { useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { httpClient } from '../lib/httpClient';
import { decodeJWT } from '../utils/jwtDecoder';

interface LoginUserInfo {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  role: 'admin' | 'advisor' | 'viewer' | 'compliance_officer';
}

export interface LoginResponse {
  // Success shape
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  account?: {
    id: string;
    name: string;
    scenario: string;
    description: string;
    expires_at: string;
    demo_scenarios: Record<string, any>;
  };
  user?: LoginUserInfo;
  // 2FA challenge shape (TASK-573 backend / TASK-631 frontend)
  status?: '2fa_required' | '2fa_enrollment_required' | string;
  challenge_token?: string;
  two_factor_required?: boolean;
}

export type LoginResult =
  | { status: 'ok' }
  | { status: '2fa_required' | '2fa_enrollment_required'; challengeToken: string };

export function useLogin() {
  const { setAccount, setUser } = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Store tokens and populate account/user context from a successful login
   * response (password login or 2FA verify-login — both share this shape).
   */
  const finalizeSession = async (response: LoginResponse, email: string): Promise<void> => {
    if (!response.access_token) {
      throw new Error('Invalid response: missing access_token');
    }
    if (!response.account?.id) {
      throw new Error('Invalid response: missing account data');
    }

    httpClient.setAuthToken(response.access_token);
    localStorage.setItem('account_id', response.account.id);

    // Fetch user settings for preferred language (TASK-168); optional.
    let preferredLanguage: string | undefined;
    try {
      const settingsResponse = await httpClient.get('/api/settings');
      preferredLanguage =
        settingsResponse.data?.preferred_language || settingsResponse.preferred_language;
    } catch {
      /* settings are optional */
    }

    // TASK-428: per-tenant UI feature flags ride the JWT. Decode defensively —
    // a malformed token must not block login.
    let features: Record<string, boolean> = {};
    try {
      features = decodeJWT(response.access_token).features ?? {};
    } catch {
      /* permissive default */
    }

    setAccount({
      id: response.account.id,
      name: response.account.name,
      email,
      scenario: response.account.scenario,
      isDemo: response.account.scenario !== 'production',
      preferred_language: preferredLanguage,
      features,
    });

    if (response.user) {
      setUser({
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.first_name,
        lastName: response.user.last_name,
        displayName: response.user.display_name,
        role: response.user.role,
      });
    }
  };

  /**
   * Login with email and password.
   * Returns {status:'ok'} on a full session, or the 2FA challenge to route on.
   */
  const login = async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await httpClient.post<LoginResponse>('/api/auth/login', {
        email,
        password,
      });

      // TASK-631: tenant requires 2FA — no tokens yet, hand back the challenge.
      if (
        (response.status === '2fa_required' || response.status === '2fa_enrollment_required') &&
        response.challenge_token
      ) {
        return { status: response.status, challengeToken: response.challenge_token };
      }

      await finalizeSession(response, email);
      return { status: 'ok' };
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { login, finalizeSession, isLoading, error };
}
